import {
  Box,
  Button,
  Container,
  Divider,
  Grid,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import React, { useContext, useEffect, useState } from "react";
import useApiEp from "../../hooks/useApiEp";
import PermDataSettingOutlinedIcon from "@mui/icons-material/PermDataSettingOutlined";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import PageTitle from "../../widgets/PageTitle";
import { toast } from "react-toastify";
import SectionLabel from "../../widgets/SectionLabel";
import { UserContext } from "../../context/UserProvider";
import { useNavigate } from "react-router-dom";
import { getHeader, toastError } from "../../utils/util";
import axios from "axios";
import { API_ENDPOINT } from "../../utils/constants";
import { LoadingButton } from "@mui/lab";

// Reusable CopyButton component
const CopyButton = ({ text, label }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      toast.success(`${label} copied to clipboard!`);
      setTimeout(() => setCopied(false), 2000); // Reset after 2 seconds
    });
  };

  return (
    <Tooltip title={copied ? "Copied!" : "Copy"}>
      <IconButton onClick={handleCopy} color={copied ? "success" : "default"}>
        {copied ? (
          <CheckCircleOutlineIcon fontSize="small" />
        ) : (
          <ContentCopyIcon fontSize="small" />
        )}
      </IconButton>
    </Tooltip>
  );
};

const ApiSettings = () => {
  const navigate = useNavigate();
  const [apiToken, setApiToken] = useState("");
  const [apiCode, setApiCode] = useState("");
  const [campaignEp, setCampaignEp] = useState("");
  const [sessionEp, setSessionEp] = useState("");
  const { user } = useContext(UserContext);
  const [generating, setGenerating] = useState(false);
  const [submittingCode, setSubmittingCode] = useState(false);
  const {
    data: apiCred,
    isLoading,
    isError,
    error,
    refetch,
  } = useApiEp(`/user/get-api-cred`);

  useEffect(() => {
    if (apiCred) {
      setApiToken(apiCred.apiToken);
      setApiCode(apiCred.apiCode);
      setCampaignEp(apiCred.endpoint.campaign);
      setSessionEp(apiCred.endpoint.session);
    }
  }, [apiCred]);

  const generateNewToken = async () => {
    try {
      setGenerating(true);
      const response = await axios.patch(
        `${API_ENDPOINT}/user/update-api-token`,
        {},
        getHeader()
      );
      toast.success(response.data.message);
      refetch();
    } catch (error) {
      toastError(error);
    } finally {
      setGenerating(false);
    }
  };

  const saveToken = async () => {
    if (!apiCode) {
      toast.error("Please enter code first!");
      return;
    }
    try {
      setSubmittingCode(true);
      const response = await axios.patch(
        `${API_ENDPOINT}/user/update-api-code`,
        { code: apiCode },
        getHeader()
      );
      toast.success(response.data.message);
      refetch();
    } catch (error) {
      toastError(error);
    } finally {
      setSubmittingCode(false);
    }
  };

  return (
    <Container sx={{ mt: 4, mb: 4 }}>
      <PageTitle mb={3}>Account Credentials</PageTitle>

      {isLoading && <Typography>Loading credentials...</Typography>}
      {isError && (
        <Typography color="error" mb={2}>
          Error: {error?.message || "Failed to load credentials"}
        </Typography>
      )}
      <Box my={3} display={"flex"} justifyContent={"center"}>
        <Typography mb={1} fontSize={"17px"} fontWeight={600}>
          {user.customerId}
        </Typography>
      </Box>
      <Grid container rowSpacing={3} columnSpacing={2}>
        <Grid item xs={12} sm={8}>
          <Stack direction="row" gap={1} alignItems="center">
            <TextField
              fullWidth
              label="API Token"
              variant="outlined"
              size="small"
              value={apiToken}
              onChange={(e) => setApiToken(e.target.value)}
              disabled={isLoading}
              InputProps={{
                readOnly: true, // Make token read-only
              }}
            />
            <CopyButton text={apiToken} label="API Token" />
          </Stack>
        </Grid>
        <Grid item xs={12} sm={4}>
          <LoadingButton
            startIcon={<PermDataSettingOutlinedIcon />}
            fullWidth
            onClick={generateNewToken}
            variant="contained"
            loading={generating}
            sx={{ height: "40px" }} // Match TextField height
          >
            Generate New Token
          </LoadingButton>
        </Grid>
        <Grid item xs={12} sm={8}>
          <Stack direction="row" gap={1} alignItems="center">
            <TextField
              fullWidth
              label="API Code"
              variant="outlined"
              size="small"
              value={apiCode}
              onChange={(e) => setApiCode(e.target.value)}
              disabled={isLoading}
            />
            <CopyButton text={apiCode} label="API Code" />
          </Stack>
        </Grid>
        <Grid item xs={12} sm={4}>
          <LoadingButton
            startIcon={<SaveOutlinedIcon />}
            fullWidth
            onClick={saveToken}
            variant="contained"
            loading={submittingCode}
            sx={{ height: "40px" }}
          >
            Save Code
          </LoadingButton>
        </Grid>
        <Grid item xs={12}>
          <Divider />
        </Grid>
        <Grid item xs={12}>
          <SectionLabel>Api Endpoints</SectionLabel>
        </Grid>

        <Grid item xs={12} sm={8}>
          <Stack direction="row" gap={1} alignItems="center">
            <TextField
              fullWidth
              label="Campaign Endpoint"
              variant="outlined"
              size="small"
              value={campaignEp}
              onChange={(e) => setCampaignEp(e.target.value)}
              disabled={isLoading}
            />
            <CopyButton text={campaignEp} label="Campaign Endpoint" />
          </Stack>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Button
            onClick={() => {
              navigate(`/home/campaign-api-docs`);
            }}
            variant="outlined"
            fullWidth
          >
            Documentation
          </Button>
        </Grid>
        <Grid item xs={12} sm={8}>
          <Stack direction="row" gap={1} alignItems="center">
            <TextField
              fullWidth
              label="Session Endpoint"
              variant="outlined"
              size="small"
              value={sessionEp}
              onChange={(e) => setSessionEp(e.target.value)}
              disabled={isLoading}
            />
            <CopyButton text={sessionEp} label="Session Endpoint" />
          </Stack>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Button
            onClick={() => {
              navigate(`/home/session-api-docs`);
            }}
            variant="outlined"
            fullWidth
          >
            Documentation
          </Button>
        </Grid>
      </Grid>
    </Container>
  );
};

export default ApiSettings;
